// Find quotes inside a text block so they can be lifted into their own
// APA Quote block, the way an image citation can be detached into its own
// object in the Slide Designer.
//
// The signal we key on is an APA *parenthetical* citation closing a paragraph:
//
//   Animation is "the process of drawing …" (Blair, n.d., p. 6).
//   Squash and stretch is the key principle (Thomas & Johnston, 1981, pp. 47-51).
//
// A narrative citation ("As Thomas and Johnston (1981) noted, …") is
// deliberately not matched: the citation sits mid-sentence, so there is no
// clean quote to lift out.

// (Author, year) with an optional page/pages, closing the paragraph.
const CITATION_PATTERN = /\(([^()]{2,160}?),\s*(n\.d\.|\d{4}[a-z]?)(?:,\s*(pp?\.\s*[^)]+?))?\)\s*[.!?]?\s*$/;

// Elements that can hold a standalone quote. List items are excluded — pulling
// one row out of a list leaves the list mangled.
const QUOTE_HOST_TAGS = new Set(['P', 'BLOCKQUOTE', 'DIV']);

const normalizeText = (value) => String(value || '')
    .replace(/ /g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Only unwrap when the text is quoted at *both* ends — stripping a lone
// trailing mark would eat the closing quote off a sentence like
// `Animation is "the process …movement"`.
const stripEnclosingQuotes = (value) => {
    const text = String(value || '').trim();
    if (!/^["“]/.test(text) || !/["”]$/.test(text)) return text;
    return text.replace(/^["“]\s*/, '').replace(/\s*["”]$/, '').trim();
};

const parseDocument = (html) => {
    if (typeof DOMParser === 'undefined') return null;
    return new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
};

/**
 * Pull the source details out of a closing parenthetical citation.
 * @returns {{author, year, page, start}|null} `start` is where the citation
 *          begins in the text, so the quote body can be split off it.
 */
export const parseTrailingCitation = (text) => {
    const match = CITATION_PATTERN.exec(normalizeText(text));
    if (!match) return null;
    const author = match[1].trim();
    // Guard against ordinary parentheses that happen to end in a year, e.g.
    // "(rewritten for the new syllabus, 2024)" — real author strings are short.
    if (!author || author.length > 80 || /[;:]/.test(author)) return null;
    return {
        author,
        year: match[2],
        // formatPageSegment re-adds "p."/"pp.", so store the bare number/range.
        page: (match[3] || '').replace(/^pp?\.\s*/i, '').trim(),
        start: match.index
    };
};

/**
 * Decide what the quote block should say for one paragraph.
 *
 * The whole sentence is kept, never just the words inside the quotation marks:
 * a short quoted term ("down shooter camera") is meaningless once lifted away
 * from the sentence that explains it.
 *
 * The one exception is a sentence that is itself nothing but a quotation — the
 * enclosing marks come off so the quote card can supply its own. Everything
 * else keeps its marks exactly as typed and comes through as a `paraphrase`,
 * because the card wraps a `quote` in curly quotation marks and would
 * otherwise put quotes inside quotes.
 */
const buildQuoteFields = (body) => {
    const quoteMarkCount = (body.match(/["“”]/g) || []).length;
    const stripped = stripEnclosingQuotes(body);
    if (stripped !== body && quoteMarkCount === 2) {
        return { quoteText: stripped, sourceType: 'quote' };
    }
    return { quoteText: body, sourceType: 'paraphrase' };
};

/**
 * Find every quote in a block's HTML.
 *
 * @param {string} html a text block's `content`
 * @returns {Array<{elementIndex, quoteText, sourceType, author, year, page, sourceText}>}
 *          in document order. `elementIndex` addresses the paragraph for
 *          removeQuoteAt().
 */
export const detectQuotes = (html) => {
    const doc = parseDocument(html);
    if (!doc) return [];

    const results = [];
    Array.from(doc.body.children).forEach((element, elementIndex) => {
        if (!QUOTE_HOST_TAGS.has(element.tagName)) return;
        const text = normalizeText(element.textContent);
        if (!text) return;

        const citation = parseTrailingCitation(text);
        if (!citation) return;

        const body = text.slice(0, citation.start).trim().replace(/[,;:]$/, '').trim();
        // A paragraph that is nothing but a citation has no quote to lift.
        if (body.length < 12) return;

        const { quoteText, sourceType } = buildQuoteFields(body);
        if (!quoteText) return;

        results.push({
            elementIndex,
            quoteText,
            sourceType,
            author: citation.author,
            year: citation.year,
            page: citation.page,
            sourceText: text
        });
    });
    return results;
};

export const hasDetectableQuote = (html) => detectQuotes(html).length > 0;

/**
 * Remove the paragraph a detected quote came from, so the quote reads once.
 * Returns the original HTML untouched when the index does not resolve.
 */
export const removeQuoteAt = (html, elementIndex) => {
    const doc = parseDocument(html);
    if (!doc) return html;
    const element = doc.body.children[elementIndex];
    if (!element) return html;
    element.remove();
    return doc.body.innerHTML;
};

/**
 * Remove several paragraphs at once. Indexes are resolved against the original
 * HTML, so removing back-to-front keeps the earlier ones valid.
 */
export const removeQuotesAt = (html, elementIndexes = []) => {
    const doc = parseDocument(html);
    if (!doc) return html;
    [...new Set(elementIndexes)]
        .sort((a, b) => b - a)
        .forEach((index) => doc.body.children[index]?.remove());
    return doc.body.innerHTML;
};

/**
 * Shape a detected quote as an `apa-quote` block's data.
 */
export const quoteToBlockData = (quote) => ({
    type: 'apa-quote',
    quoteText: quote.quoteText,
    sourceType: quote.sourceType,
    author: quote.author,
    year: quote.year,
    page: quote.page,
    sources: [{ author: quote.author, year: quote.year, page: quote.page }]
});

/** Short label for a menu row, e.g. "Blair, n.d., p. 6". */
export const describeQuote = (quote) => [
    quote.author,
    quote.year,
    quote.page ? `p. ${quote.page}` : ''
].filter(Boolean).join(', ');
